/*!
 * Natren first-party attribution tracker.
 *
 * Runs on the storefront (theme asset). Does three things:
 *   1. Remembers how this browser first arrived, and how it arrived this visit.
 *   2. Stamps that onto the Shopify cart, so the finished ORDER carries its own
 *      source and no after-the-fact matching is needed.
 *   3. Beacons session and funnel events to our ingest endpoint.
 *
 * Deliberately dumb about channels: it sends raw signals (referrer, UTMs, click
 * ids) and lets the server classify. One set of rules, server-side, so a change
 * never needs a theme deploy.
 *
 * Never throws into the page. Every entry point is wrapped; a tracking failure
 * must not break a storefront.
 */
(function () {
  'use strict';

  var CFG = window.NATREN_ATTR || {};
  var ENDPOINT   = CFG.endpoint || 'https://natren-ltv.vercel.app/api/attr/collect';
  var DEBUG      = !!CFG.debug || /natren_attr_debug=1/.test(location.search);
  var SESSION_MINUTES = 30;
  // How long an unconverted first touch stays credited. Measured on Natren's
  // own orders: 79% of new customers buy within a day, 90% within a week, 99%
  // within 28 days. Past that, crediting the old ad is not defensible, so the
  // next visit becomes the acquisition source. Matches Google Ads' default
  // click window, which keeps our numbers comparable to theirs.
  var FIRST_TOUCH_DAYS = CFG.firstTouchDays || 30;

  var K_ANON  = 'natren_anon_id';
  var K_FIRST = 'natren_first_touch';
  var K_SESS  = 'natren_session';
  var K_SENT  = 'natren_session_sent';
  var K_LAST  = 'natren_last_touch';

  var CLICK_IDS = ['gclid', 'gbraid', 'wbraid', 'fbclid', 'msclkid', 'ttclid'];
  var UTMS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'];

  function log() { if (DEBUG && window.console) console.log.apply(console, ['[natren-attr]'].concat([].slice.call(arguments))); }

  // ---------------------------------------------------------------- storage
  function get(store, k) { try { return store.getItem(k); } catch (e) { return null; } }
  function set(store, k, v) { try { store.setItem(k, v); } catch (e) {} }
  function getJSON(store, k) { try { return JSON.parse(get(store, k) || 'null'); } catch (e) { return null; } }

  function uuid() {
    try { if (crypto && crypto.randomUUID) return crypto.randomUUID(); } catch (e) {}
    return 'a-' + Date.now().toString(36) + '-' + Math.random().toString(36).slice(2, 12);
  }

  // ---------------------------------------------------------------- consent
  // If Shopify's privacy API says analytics is not allowed, we record nothing.
  function allowed() {
    try {
      var api = window.Shopify && window.Shopify.customerPrivacy;
      if (api && typeof api.analyticsProcessingAllowed === 'function') {
        return api.analyticsProcessingAllowed();
      }
    } catch (e) {}
    return true; // no consent gate present on this storefront
  }

  // ---------------------------------------------------------------- signals
  function params() {
    var out = {};
    try {
      var sp = new URLSearchParams(location.search);
      UTMS.concat(CLICK_IDS).forEach(function (k) {
        var v = sp.get(k);
        if (v) out[k] = v.slice(0, 400);
      });
    } catch (e) {}
    return out;
  }

  function cookie(name) {
    try {
      var m = document.cookie.match(new RegExp('(?:^|; )' + name + '=([^;]*)'));
      return m ? decodeURIComponent(m[1]) : null;
    } catch (e) { return null; }
  }

  function device() {
    var w = window.innerWidth || 0;
    if (/Mobi|Android|iPhone/i.test(navigator.userAgent) && w < 768) return 'mobile';
    if (/iPad|Tablet/i.test(navigator.userAgent) || (w >= 768 && w < 1024)) return 'tablet';
    return 'desktop';
  }

  function snapshot() {
    var p = params();
    // Meta's own cookies materially improve match quality later
    if (cookie('_fbc')) p._fbc = cookie('_fbc');
    if (cookie('_fbp')) p._fbp = cookie('_fbp');
    return {
      params: p,
      landing_page: location.href.split('#')[0].slice(0, 900),
      landing_path: location.pathname.slice(0, 300),
      referrer: (document.referrer || '').slice(0, 900),
      at: new Date().toISOString()
    };
  }

  // ---------------------------------------------------------------- identity
  function anonId() {
    var id = get(localStorage, K_ANON);
    if (!id) { id = uuid(); set(localStorage, K_ANON, id); }
    return id;
  }

  /** Returns {id, isNew}. A session ends after 30 minutes of inactivity. */
  function session() {
    var now = Date.now();
    var s = getJSON(sessionStorage, K_SESS);
    if (s && s.id && (now - (s.t || 0)) < SESSION_MINUTES * 60000) {
      s.t = now; set(sessionStorage, K_SESS, JSON.stringify(s));
      return { id: s.id, isNew: false };
    }
    var fresh = { id: uuid(), t: now };
    set(sessionStorage, K_SESS, JSON.stringify(fresh));
    return { id: fresh.id, isNew: true };
  }

  /**
   * First touch, with a lookback window.
   *
   * Held until it ages out, not for the life of the browser. Once the visitor
   * actually buys, the acquisition is recorded permanently server-side against
   * the customer, so the copy kept here stops mattering at that point.
   */
  function firstTouch(snap) {
    var existing = getJSON(localStorage, K_FIRST);
    if (existing && existing.at) {
      var ageDays = (Date.now() - Date.parse(existing.at)) / 86400000;
      if (ageDays >= 0 && ageDays < FIRST_TOUCH_DAYS) return existing;
      snap.resets = (existing.resets || 0) + 1;
      log('first touch expired after ' + Math.round(ageDays) + ' days, replacing');
    } else if (existing) {
      return existing; // written before the window existed; leave it alone
    }
    set(localStorage, K_FIRST, JSON.stringify(snap));
    return snap;
  }

  /** Does this page view actually carry acquisition signal? */
  function hasAttribution(params) {
    var p = params || {};
    for (var i = 0; i < UTMS.length; i++) { if (p[UTMS[i]]) return true; }
    for (var j = 0; j < CLICK_IDS.length; j++) { if (p[CLICK_IDS[j]]) return true; }
    return false;
  }

  /**
   * Last touch, held for the visit.
   *
   * This used to be the CURRENT page's parameters. Browsing from the ad landing
   * page to the cart therefore overwrote the real last touch with blanks, and
   * every order shipped natren_last_utm="||||" - all 27 of them, including
   * Google Ads and email orders that plainly had UTMs. Only replace the stored
   * touch when the new one actually carries something.
   */
  function lastTouch(snap) {
    var stored = getJSON(sessionStorage, K_LAST);
    if (hasAttribution(snap.params)) {
      set(sessionStorage, K_LAST, JSON.stringify(snap));
      return snap;
    }
    if (stored) return stored;
    // nothing now and nothing stored: an untagged visit is itself the last touch
    set(sessionStorage, K_LAST, JSON.stringify(snap));
    return snap;
  }

  // ---------------------------------------------------------------- transport
  function send(body) {
    try {
      var payload = JSON.stringify(body);
      if (navigator.sendBeacon) {
        // text/plain keeps this a CORS "simple request". sendBeacon always
        // uses credentials mode "include", so an application/json body would
        // force a preflight that a beacon cannot satisfy.
        var blob = new Blob([payload], { type: 'text/plain;charset=UTF-8' });
        if (navigator.sendBeacon(ENDPOINT, blob)) { log('beacon', body.kind, body); return; }
      }
      fetch(ENDPOINT, {
        method: 'POST', headers: { 'Content-Type': 'text/plain;charset=UTF-8' },
        body: payload, keepalive: true, mode: 'cors'
      }).catch(function () {});
      log('fetch', body.kind, body);
    } catch (e) { log('send failed', e); }
  }

  // ---------------------------------------------------------------- cart stamp
  /*
   * Writing attribution onto the cart is what makes the ORDER self-describing:
   * Shopify carries cart attributes through checkout onto the order, so no
   * matching is needed afterwards.
   *
   * Three things make this harder than it looks, all observed on the live cart:
   *
   *   1. Shopify does not keep attributes on an EMPTY cart. Stamping on the
   *      add-to-cart click races the /cart/add.js that follows, and the
   *      attributes are silently dropped.
   *   2. Ordergroove writes its own cart attributes (og__session, and per
   *      action og__<timestamp> keys carrying the subscription opt-ins) after
   *      us. Those keys ARE the subscription mechanism and must never be lost.
   *   3. Any later cart mutation is another chance for our keys to disappear.
   *
   * So never write blind: read the cart, merge our keys into whatever is
   * already there, write the union back, then read again to confirm it stuck.
   * And reassert after every cart mutation rather than trusting the first write.
   */
  var OUR_PREFIX = 'natren_';
  var stamping = false;
  var lastStampToken = null;

  function ourAttributes(state) {
    var ft = state.first.params || {};
    var lt = state.last.params || {};
    return {
      'natren_anon_id': state.anon,
      'natren_session_id': state.session,
      'natren_first_landing': state.first.landing_page || '',
      'natren_first_referrer': state.first.referrer || '',
      'natren_first_utm': [ft.utm_source, ft.utm_medium, ft.utm_campaign, ft.utm_content, ft.utm_term]
        .map(function (x) { return x || ''; }).join('|'),
      'natren_last_utm': [lt.utm_source, lt.utm_medium, lt.utm_campaign, lt.utm_content, lt.utm_term]
        .map(function (x) { return x || ''; }).join('|'),
      'natren_click_id': CLICK_IDS.map(function (k) {
        return (ft[k] || lt[k]) ? k + '=' + (ft[k] || lt[k]) : '';
      }).filter(Boolean).join('|'),
      'natren_last_referrer': state.last.referrer || ''
    };
  }

  // bypass our own interceptor so re-stamping cannot recurse
  function rawFetch(url, opts) {
    var f = window.__natrenRawFetch || window.fetch;
    return f.call(window, url, opts);
  }

  function stampCart(state, why) {
    if (stamping) return Promise.resolve(false);
    stamping = true;
    return rawFetch('/cart.js', { credentials: 'same-origin' })
      .then(function (r) { return r.json(); })
      .then(function (cart) {
        // An empty cart cannot hold attributes. Wait until there is something
        // in it; the cart watcher will call us again after the add.
        if (!cart || !cart.item_count) { log('cart empty, not stamping yet (' + why + ')'); return false; }

        var existing = cart.attributes || {};
        var ours = ourAttributes(state);

        var same = Object.keys(ours).every(function (k) { return existing[k] === ours[k]; });
        if (same && cart.token === lastStampToken) return true;

        // Merge: everything already on the cart first, ours on top. Ordergroove's
        // og__ keys and any other app's attributes survive untouched.
        var merged = {};
        Object.keys(existing).forEach(function (k) { merged[k] = existing[k]; });
        Object.keys(ours).forEach(function (k) { merged[k] = ours[k]; });

        return rawFetch('/cart/update.js', {
          method: 'POST', credentials: 'same-origin',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ attributes: merged })
        })
          .then(function () {
            // verify: a concurrent write can still beat us
            return rawFetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
          })
          .then(function (after) {
            var attrs = (after && after.attributes) || {};
            var ok = attrs['natren_anon_id'] === state.anon;
            var lost = Object.keys(existing).filter(function (k) {
              return k.indexOf(OUR_PREFIX) !== 0 && attrs[k] === undefined;
            });
            if (lost.length) log('WARNING: other cart attributes were lost:', lost);
            if (ok) {
              lastStampToken = after.token;
              log('cart stamped (' + why + ')', after.token, attrs);
            } else {
              log('stamp did not stick (' + why + '), will retry on the next cart change');
            }
            return ok;
          });
      })
      .catch(function (e) { log('stamp failed', e); return false; })
      .then(function (ok) { stamping = false; return ok; });
  }

  /**
   * Reassert after anything that changes the cart. The theme, Ordergroove and
   * any installed app can all mutate it, and each mutation is a chance for our
   * keys to be dropped, so we never assume the first write survives.
   */
  function watchCart(state) {
    var CART_WRITE = /\/cart\/(add|change|update|clear)(\.js)?/i;
    var timer = null;
    function schedule(why) {
      clearTimeout(timer);
      timer = setTimeout(function () { stampCart(state, why); }, 300);
    }

    try {
      var origFetch = window.fetch;
      window.__natrenRawFetch = origFetch;
      window.fetch = function (input) {
        var url = '';
        try { url = typeof input === 'string' ? input : (input && input.url) || ''; } catch (e) {}
        var p = origFetch.apply(this, arguments);
        if (url && CART_WRITE.test(url) && !stamping) {
          try { p.then(function () { schedule('after ' + url.split('?')[0]); }).catch(function () {}); } catch (e) {}
        }
        return p;
      };
    } catch (e) { log('could not wrap fetch', e); }

    try {
      var origOpen = XMLHttpRequest.prototype.open;
      XMLHttpRequest.prototype.open = function (method, url) {
        try {
          if (url && CART_WRITE.test(String(url))) {
            this.addEventListener('load', function () { if (!stamping) schedule('after xhr cart write'); });
          }
        } catch (e) {}
        return origOpen.apply(this, arguments);
      };
    } catch (e) { log('could not wrap XHR', e); }

    // last line of defence, as the buyer leaves for checkout
    document.addEventListener('click', function (e) {
      try {
        var el = e.target && e.target.closest
          ? e.target.closest('[href*="/checkout"], [name="checkout"], form[action*="/checkout"] button')
          : null;
        if (el) stampCart(state, 'checkout click');
      } catch (err) {}
    }, true);

    window.addEventListener('pagehide', function () { stampCart(state, 'pagehide'); });
  }

  // ---------------------------------------------------------------- main
  function boot() {
    if (!allowed()) { log('analytics consent not granted, standing down'); return; }

    var anon = anonId();
    var snap = snapshot();
    var first = firstTouch(snap);
    var sess = session();
    var last = lastTouch(snap);
    var state = { anon: anon, session: sess.id, first: first, last: last };

    var base = {
      anon_id: anon,
      session_id: sess.id,
      device: device(),
      user_agent: (navigator.userAgent || '').slice(0, 400),
      first: first,
      last: last
    };

    if (sess.isNew || !get(sessionStorage, K_SENT)) {
      set(sessionStorage, K_SENT, '1');
      send(Object.assign({ kind: 'session_start', is_first_session: !getJSON(localStorage, K_FIRST + '_seen') }, base));
      set(localStorage, K_FIRST + '_seen', '1');
    } else {
      send(Object.assign({ kind: 'pageview' }, base));
    }

    if (/\/products\//.test(location.pathname)) send(Object.assign({ kind: 'product_view' }, base));

    document.addEventListener('click', function (e) {
      try {
        var el = e.target && e.target.closest
          ? e.target.closest('[name="add"], .add-to-cart, [href*="/checkout"], [name="checkout"]')
          : null;
        if (!el) return;
        var checkout = /checkout/i.test(el.getAttribute('href') || '') || el.getAttribute('name') === 'checkout';
        send(Object.assign({ kind: checkout ? 'checkout_click' : 'add_to_cart' }, base));
      } catch (err) {}
    }, true);

    watchCart(state);
    stampCart(state, 'page load');

    window.NatrenAttr = {
      state: state,
      stamp: function () { return stampCart(state, 'manual'); },
      check: function () {
        return rawFetch('/cart.js', { credentials: 'same-origin' })
          .then(function (r) { return r.json(); })
          .then(function (c) { console.log('[natren-attr] cart attributes:', c.attributes); return c.attributes; });
      },
      debug: DEBUG
    };
    log('ready', state);
  }

  try {
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', boot);
    else boot();
  } catch (e) { /* never break the storefront */ }
})();
